/**
 * FikoTakim3DViewer — Takım Tanımı dialoğundaki 2D SVG profilinin 3D karşılığı.
 * Takım dönel-simetrik olduğundan, tipe göre üretilen radyal profil (yarıçap-yükseklik) THREE.LatheGeometry
 * ile 360° döndürülerek gerçek 3D takım elde edilir (tüm tipler tek profil fonksiyonundan).
 * Paylaşılan tek-renderer base (FikoCamMini3DBase) — context tüketmez. Sağ=döndür · orta=kaydır · teker=zoom.
 */
import { Component, ChangeDetectionStrategy, ChangeDetectorRef, Injector, ViewContainerRef, EnvironmentInjector, NgZone, ViewChild, ElementRef, Input, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as THREE from 'three';
import { FikoCamMini3DBase, camMiniRenderer } from './fiko-cam-mini-3d.base';
import { ITakim } from './fiko-cam.types';

/** Gerçekçi metal yansıması için PMREM ortam haritası — bir kez üret, tüm takım viewer'larında paylaş. */
let _envCache: THREE.Texture | null = null;
async function gercekciOrtam(renderer: THREE.WebGLRenderer): Promise<THREE.Texture | null> {
  if (_envCache) return _envCache;
  try {
    const mod: any = await import('three/examples/jsm/environments/RoomEnvironment.js');
    const pmrem = new THREE.PMREMGenerator(renderer);
    _envCache = pmrem.fromScene(new mod.RoomEnvironment(), 0.04).texture;
    pmrem.dispose();
  } catch (e) { _envCache = null; }
  return _envCache;
}

@Component({
  selector: 'fiko-takim-3d',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<div #viewerContainer class="tk3d-vc"></div>`,
  styles: [`
    :host{display:block;width:100%;height:100%;}
    .tk3d-vc{position:relative;width:100%;height:100%;min-height:300px;overflow:hidden;border-radius:8px;border:1px solid rgba(0,200,255,.25);background:radial-gradient(circle at 50% 30%,rgba(0,60,90,.35),rgba(0,16,30,.5));}
    .tk3d-vc ::ng-deep canvas{display:block;width:100%!important;height:100%!important;}
  `]
})
export class FikoTakim3DViewer extends FikoCamMini3DBase implements OnChanges {
  @ViewChild('viewerContainer', { static: true }) viewerContainerRef!: ElementRef<HTMLDivElement>;
  @Input() tk!: ITakim;

  private grup: THREE.Group | null = null;
  private _ext = 40;

  constructor(cd: ChangeDetectorRef, injector: Injector, viewContainerRef: ViewContainerRef, environmentInjector: EnvironmentInjector, ngZone: NgZone) {
    super(cd, injector, viewContainerRef, environmentInjector, ngZone);
    this.viewerGenislik = 220; this.viewerYukseklik = 320;
    this.toneMappingTip = 'aces'; this.toneMappingPoz = 1.05; this.ssaa = 2;   // fotorealistik render
  }
  override sahneHazir(): void { this.ciz(); this.ortamYukle(); setTimeout(() => this.cercevele(), 80); }
  /** Gerçekçi metal yansıması için sahne ortam haritasını yükle (async, hazır olunca yeniden çiz). */
  private async ortamYukle(): Promise<void> {
    const env = await gercekciOrtam(camMiniRenderer());
    if (env && this.sahne) { this.sahne.environment = env; this.iste(); }
  }
  ngOnChanges(): void { if (this.hazirMi) this.ciz(); }
  /** Dialog tk'yi YERİNDE değiştirdiğinde çağrılır (referans değişmediği için ngOnChanges tetiklenmez).
   * cerceve=true → tip değişimi gibi büyük değişimde yeniden çerçevele; ölçü düzenlemede kamera korunur. */
  guncelle(cerceve = false): void { if (!this.hazirMi) return; this.ciz(); if (cerceve) this.cercevele(); }

  private ciz(): void {
    const s: any = this.sahne; const t = this.tk; if (!s || !t) return;
    if (this.grup) { s.remove(this.grup); this.grup.traverse((o: any) => { o.geometry?.dispose?.(); const m = o.material; if (m) (Array.isArray(m) ? m : [m]).forEach((x: any) => { x.map?.dispose?.(); x.dispose?.(); }); }); }
    const g = new THREE.Group(); g.name = 'Takim3D'; this.grup = g;

    const cap = Math.max(0.5, t.cap || 6);
    const Lc = Math.max(1, t.boy || cap * 3);
    const L = Math.max(Lc + 1, t.toplamBoy || Lc * 2.5);
    const sapCap = t.sapCap || cap;
    const maxR = Math.max(cap, t.cap2 || 0, t.cap3 || 0, sapCap) / 2;
    this._ext = Math.max(L, maxR * 2);

    // Kesici gövde — radyal profil + GERÇEK bükümlü (helisel) flütler gövdeye oyulur. Parlak çelik/krom.
    const profil = this.profil3d(t, Lc);
    if (profil.length >= 2) {
      const fp = this.flutParam(t, Lc);
      const geo = this.fluteliGeo(profil, fp.N, fp.g, fp.twist, Lc);
      const mat = new THREE.MeshPhysicalMaterial({ vertexColors: true, metalness: 0.95, roughness: 0.32, clearcoat: 0.5, clearcoatRoughness: 0.18, envMapIntensity: 1.35, side: THREE.DoubleSide });
      const kesici = new THREE.Mesh(geo, mat); g.add(kesici);
    }
    // Sap (shank) — silindir, kesicinin üstünde (parlak işlenmiş çelik)
    const sapR = (t.sapCap ? t.sapCap / 2 : maxR * 0.62);
    const sapH = Math.max(0.5, L - Lc);
    const sapMat = new THREE.MeshPhysicalMaterial({ color: 0xaeb9c6, metalness: 0.96, roughness: 0.3, clearcoat: 0.4, clearcoatRoughness: 0.22, envMapIntensity: 1.25 });
    const sap = new THREE.Mesh(new THREE.CylinderGeometry(sapR, sapR, sapH, 64), sapMat);
    sap.position.y = Lc + sapH / 2; g.add(sap);
    // sap-kesici geçiş omuzu (kesici üst yarıçapından sapR'ye)
    const ust = profil.length ? profil[profil.length - 1].x : maxR;
    if (Math.abs(ust - sapR) > 0.05) {
      const omuz = new THREE.Mesh(new THREE.CylinderGeometry(sapR, ust, Math.max(0.4, cap * 0.12), 64), sapMat);
      omuz.position.y = Lc; g.add(omuz);
    }

    // 3D ÖLÇÜ ETİKETLERİ (Ø / Lc / L) — takımdan biraz UZAKTA dursun
    const R = cap / 2; const u = this._ext;
    g.add(this.etiket('Ø' + this.k(cap), 0xffd9a0, R + u * 0.28, -u * 0.04, 0));
    g.add(this.etiket('Lc ' + this.k(Lc), 0x9fe6ff, -(R + u * 0.32), Lc / 2, 0));
    g.add(this.etiket('L ' + this.k(L), 0xffb74d, R + u * 0.42, L * 0.5, 0));
    g.add(this.etiket('Ds ' + this.k(sapCap), 0xb7c4d6, -(sapCap / 2 + u * 0.3), Lc + (L - Lc) * 0.6, 0));   // sap çapı

    // Tool no
    g.add(this.etiket('T' + (t.numara ?? '?'), 0xffe08a, 0, L + u * 0.16, 0));

    // Y-yukarı modelden Z-yukarı sahneye (base kamera Z-up, izometrik)
    g.rotation.x = Math.PI / 2;
    s.add(g);
    if (typeof s.anaGosterim?.ciz === 'function') s.anaGosterim.ciz();
  }

  /** Tipe göre flüt parametreleri: N (ağız), g (oluk derinliği oranı), twist (helis tur sayısı). */
  private flutParam(t: ITakim, Lc: number): { N: number; g: number; twist: number } {
    const tip = t.tip, R = Math.max(0.5, (t.cap || 6) / 2);
    const milling = ['endMill', 'faceMill', 'ballMill', 'bullMill'].includes(tip);
    const drilling = ['drill', 'spotDrill', 'multiDrill', 'centerDrill'].includes(tip);
    let N = Math.min(6, Math.max(2, t.agizSayisi || (drilling ? 2 : 4)));
    if (drilling) N = 2;                                          // matkaplar 2 flüt
    let g = milling ? 0.24 : drilling ? 0.32 : ['chamferMill', 'countersink', 'conicalMill'].includes(tip) ? 0.16 : 0;
    let twist = g > 0 ? Math.max(1.1, Lc / (R * 4.5)) : 0;        // helis tur
    if (['reamer', 'tap', 'threadMill', 'boringBar', 'tslotMill'].includes(tip)) { g = 0; twist = 0; }
    return { N, g, twist };
  }

  /** Radyal profil + N flüt + helis büküm → GERÇEK flütlü katı gövde (BufferGeometry + vertex renk).
   * Flüt kesiti: dönem içinde geniş LAND (tam yarıçap, kesici ağız) + keskin geçişli GULLET oyuğu (sin scoop).
   * Gullet'ler vertex renkle koyulaştırılır → recess/gölge hissi (gerçekçi). g=0 → düz revolve. */
  private fluteliGeo(profil: THREE.Vector2[], N: number, g: number, twistTurns: number, Lc: number): THREE.BufferGeometry {
    const rings: { y: number; r: number }[] = []; const SUB = 10;
    for (let s = 0; s < profil.length - 1; s++) {
      const a = profil[s], b = profil[s + 1];
      for (let i = 0; i < SUB; i++) { const u = i / SUB; rings.push({ y: a.y + (b.y - a.y) * u, r: a.x + (b.x - a.x) * u }); }
    }
    rings.push({ y: profil[profil.length - 1].y, r: profil[profil.length - 1].x });
    const M = 96, twistRate = twistTurns * Math.PI * 2 / Math.max(1, Lc), per = Math.PI * 2 / Math.max(1, N), land = 0.5;
    const pos: number[] = [], col: number[] = [], idx: number[] = [], ringV: number[] = [];
    const acik = [0.82, 0.88, 0.95], koyu = [0.30, 0.35, 0.41];   // land parlak çelik / gullet koyu
    for (const rg of rings) {
      ringV.push(pos.length / 3);
      for (let m = 0; m < M; m++) {
        const th = m / M * Math.PI * 2;
        let cut = 0;
        if (g > 0) { const phi = (((th + twistRate * rg.y) % per) + per) % per / per; if (phi > land) { const u = (phi - land) / (1 - land); cut = g * Math.sin(Math.PI * u); } }
        const rr = Math.max(0, rg.r * (1 - cut));
        pos.push(rr * Math.cos(th), rg.y, rr * Math.sin(th));
        const t = g > 0 ? 1 - cut / g : 1;                        // 1=land .. 0=oyuk dibi
        col.push(koyu[0] + (acik[0] - koyu[0]) * t, koyu[1] + (acik[1] - koyu[1]) * t, koyu[2] + (acik[2] - koyu[2]) * t);
      }
    }
    for (let ri = 0; ri < rings.length - 1; ri++) {
      const a = ringV[ri], b = ringV[ri + 1];
      for (let m = 0; m < M; m++) { const m2 = (m + 1) % M; idx.push(a + m, a + m2, b + m, b + m, a + m2, b + m2); }
    }
    const renkEkle = () => { col.push(acik[0], acik[1], acik[2]); };
    if (rings[0].r > 0.01) { const c = pos.length / 3; pos.push(0, rings[0].y, 0); renkEkle(); const a = ringV[0]; for (let m = 0; m < M; m++) idx.push(c, a + (m + 1) % M, a + m); }        // alt kapak
    const lst = rings.length - 1, cT = pos.length / 3; pos.push(0, rings[lst].y, 0); renkEkle(); const aT = ringV[lst]; for (let m = 0; m < M; m++) idx.push(cT, aT + m, aT + (m + 1) % M);   // üst kapak
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
    geo.setIndex(idx); geo.computeVertexNormals();
    return geo;
  }

  /** Tipe göre radyal profil: Vector2(x=yarıçap mm, y=uçtan yükseklik mm). Uç y=0, kesici üstü y=Lc. */
  private profil3d(t: ITakim, Lc: number): THREE.Vector2[] {
    const R = (t.cap || 6) / 2, tip = t.tip;
    const P = (x: number, y: number) => new THREE.Vector2(Math.max(0, x), y);
    const aci = (a: number, d: number) => Math.max(1, Math.min(179, a || d)) * Math.PI / 180;
    const yay = (cx: number, cy: number, r: number, a0: number, a1: number, n = 10): THREE.Vector2[] => {
      const o: THREE.Vector2[] = []; for (let i = 0; i <= n; i++) { const a = a0 + (a1 - a0) * i / n; o.push(P(cx + r * Math.cos(a), cy + r * Math.sin(a))); } return o;
    };
    if (tip === 'ballMill') return [P(0, 0), ...yay(0, R, R, -Math.PI / 2, 0), P(R, Lc)];
    if (tip === 'bullMill') { const k = Math.min(R, t.kose || 0); return [P(0, 0), P(R - k, 0), ...yay(R - k, k, k, -Math.PI / 2, 0), P(R, Lc)]; }
    if (tip === 'drill' || tip === 'spotDrill') { const h = R / Math.tan(aci(t.ucAci, 118) / 2); return [P(0, 0), P(R, h), P(R, Lc)]; }
    if (tip === 'centerDrill') { const rp = R * 0.5, hTip = rp / Math.tan(aci(t.ucAci, 90) / 2), hTap = (R - rp) / Math.tan(aci(t.ucAci2, 60) / 2); return [P(0, 0), P(rp, hTip), P(rp, hTip), P(R, hTip + hTap), P(R, Lc)]; }
    if (tip === 'conicalMill') { const rb = (t.girisCap ? t.girisCap / 2 : R * 0.55); return [P(0, 0), P(rb, 0), P(R, Lc)]; }
    if (tip === 'countersink' || tip === 'chamferMill') { const h = R / Math.tan(aci(t.ucAci, 90) / 2), rd = (t.girisCap ? t.girisCap / 2 : R * 0.25); return [P(0, 0), P(rd, 0), P(rd, h * 0.15), P(R, h), P(R, Lc)]; }
    if (tip === 'multiDrill') {
      const r1 = R, r2 = (t.cap2 || t.cap) / 2, r3 = (t.cap3 || t.cap2 || t.cap) / 2;
      const hTip = r1 / Math.tan(aci(t.ucAci, 118) / 2), l1 = (t.kademeBoy1 || t.boy * 0.3), l2 = (t.kademeBoy2 || t.boy * 0.3);
      const yk1 = hTip, yk2 = yk1 + l1, yk3 = yk2 + l2;
      return [P(0, 0), P(r1, yk1), P(r1, yk2), P(r2, yk2), P(r2, yk3), P(r3, yk3), P(r3, Lc)];
    }
    if (tip === 'counterbore') { const rd = (t.girisCap ? t.girisCap / 2 : R * 0.5), l1 = (t.kademeBoy1 || t.boy * 0.4); return [P(0, 0), P(rd, 0), P(rd, l1), P(R, l1), P(R, Lc)]; }
    if (tip === 'tslotMill') { const neck = R * 0.4, diskH = Lc * 0.4; return [P(0, 0), P(R, 0), P(R, diskH), P(neck, diskH), P(neck, Lc)]; }
    if (tip === 'boringBar') { const rn = R * 0.4; return [P(0, 0), P(R, R * 0.4), P(R, R * 0.6), P(rn, R * 0.6), P(rn, Lc)]; }
    if (tip === 'reamer' || tip === 'tap') return [P(0, 0), P(R, R * 0.3), P(R, Lc)];
    // faceMill / endMill / threadMill: düz dip
    return [P(0, 0), P(R, 0), P(R, Lc)];
  }

  private k(n: number): string { return (+(+n || 0).toFixed(2)).toString(); }
  private etiket(metin: string, renk: number, x: number, y: number, z: number): THREE.Sprite {
    const W = 200, H = 64, cv = document.createElement('canvas'); cv.width = W; cv.height = H; const ctx = cv.getContext('2d')!;
    const hex = '#' + renk.toString(16).padStart(6, '0');
    ctx.fillStyle = 'rgba(8,18,30,.9)'; this.rdik(ctx, 4, 4, W - 8, H - 8, 12); ctx.fill();
    ctx.strokeStyle = hex; ctx.lineWidth = 3; this.rdik(ctx, 4, 4, W - 8, H - 8, 12); ctx.stroke();
    ctx.fillStyle = '#eaf6ff'; ctx.font = 'bold 30px system-ui,sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText(metin, W / 2, H / 2 + 1);
    const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace;
    const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true, toneMapped: false }));
    const sc = Math.max(2.5, this._ext * 0.11); sp.scale.set(sc * (W / H), sc, 1); sp.position.set(x, y, z); sp.renderOrder = 999; return sp;
  }
  private rdik(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) { ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath(); }

  private cercevele(): void {
    const ag: any = (this.sahne as any)?.anaGosterim; const kam = ag?.aktifKamera; if (!kam) return;
    const L = this._ext, d = Math.max(20, L * 2.4);
    kam.position.set(d, -d, d * 0.7); if (kam.lookAt) kam.lookAt(0, 0, L * 0.45);
    if (kam.isOrthographicCamera) { kam.zoom = Math.max(0.6, 150 / Math.max(1, L)); kam.updateProjectionMatrix?.(); }
    if (ag.aktifKontrol?.target) { ag.aktifKontrol.target.set(0, 0, L * 0.45); ag.aktifKontrol.update?.(); }
    ag.ciz?.();
  }
}
